/** Tipo de suministro deducido de las fases con tensión. */
export type TipoSuministro =
  'monofasico' | 'bifasico' | 'trifasico' | 'sin_tension' | 'desconocido';

const ETIQUETA_SUMINISTRO: Readonly<Record<TipoSuministro, string>> = {
  monofasico: 'Monofásico',
  bifasico: 'Bifásico',
  trifasico: 'Trifásico',
  sin_tension: 'Sin tensión',
  desconocido: 'Sin datos',
};

/**
 * Tensión por debajo de la cual se considera que la fase no está alimentada.
 * Un medidor sin carga reporta unos pocos voltios de ruido.
 */
const UMBRAL_FASE_ACTIVA_V = 10;

const RAIZ_3 = Math.sqrt(3);

export interface MedidorProps {
  /** Identificador del medidor: es un UUID, no un número. */
  id: string;
  serial: string | null;
  nombre: string | null;
  clienteId: number | null;
  clienteNombre: string | null;
  origen: string | null;
  protocolo: string | null;
  gateway: string | null;
  /** Nombre del secreto con la credencial de lectura. */
  claveMedicion: string | null;
  nodoId: number | null;
  sondeoActivo: boolean;
  intervaloSondeoSegundos: number | null;
  /** Variables tal como las entrega el medidor. */
  lecturas: Record<string, unknown>;
  ultimaLectura: Date | null;
  lecturaCorrecta: boolean | null;
  mensajeError: string | null;
}

/**
 * Convierte a número los valores del medidor, que llegan como texto.
 * Devuelve `null` para lo que no sea un número utilizable.
 */
function aNumeroMedidor(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = typeof valor === 'number' ? valor : Number(String(valor).trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Medidor de energía eléctrica.
 *
 * Sabe interpretar sus propias lecturas: qué fases están alimentadas, qué tipo
 * de suministro es y cuál es la tensión que corresponde mostrar. Esa
 * interpretación no es evidente y estaba escrita dentro del renderizado de la
 * versión anterior, donde no se podía comprobar ni reutilizar.
 */
export class Medidor {
  constructor(private readonly props: MedidorProps) {}

  get id(): string {
    return this.props.id;
  }
  get serial(): string {
    return this.props.serial ?? '—';
  }
  get nombre(): string {
    return this.props.nombre?.trim() || this.props.serial || 'Medidor sin nombre';
  }
  get clienteId(): number | null {
    return this.props.clienteId;
  }
  get clienteNombre(): string | null {
    return this.props.clienteNombre;
  }
  get protocolo(): string {
    return this.props.protocolo?.toUpperCase() ?? '—';
  }
  get origen(): string | null {
    return this.props.origen;
  }
  get gateway(): string | null {
    return this.props.gateway;
  }
  get claveMedicion(): string | null {
    return this.props.claveMedicion;
  }

  get nodoId(): number | null {
    return this.props.nodoId;
  }

  get sondeoActivo(): boolean {
    return this.props.sondeoActivo;
  }
  get intervaloSondeoSegundos(): number | null {
    return this.props.intervaloSondeoSegundos;
  }
  get ultimaLectura(): Date | null {
    return this.props.ultimaLectura;
  }
  get mensajeError(): string | null {
    return this.props.mensajeError;
  }

  /** `null` si el medidor todavía no ha entregado ninguna lectura. */
  get lecturaCorrecta(): boolean | null {
    return this.props.lecturaCorrecta;
  }

  get tieneLecturas(): boolean {
    return Object.keys(this.props.lecturas).length > 0;
  }

  /** Valor numérico de una variable del medidor. */
  valor(variable: string): number | null {
    return aNumeroMedidor(this.props.lecturas[variable]);
  }

  /** Tensión de cada fase, en orden. */
  get tensionesPorFase(): (number | null)[] {
    return [1, 2, 3].map((i) => this.valor(`RMS_Volts_Ln_${i}`));
  }

  /** Corriente de cada fase, en orden. */
  get corrientesPorFase(): (number | null)[] {
    return [1, 2, 3].map((i) => this.valor(`Amps_Ln_${i}`));
  }

  /** Índices (base 0) de las fases que tienen tensión. */
  private get fasesActivas(): number[] {
    return this.tensionesPorFase
      .map((v, i) => (v !== null && v > UMBRAL_FASE_ACTIVA_V ? i : -1))
      .filter((i) => i >= 0);
  }

  get tipoSuministro(): TipoSuministro {
    const activas = this.fasesActivas.length;
    if (activas === 3) return 'trifasico';
    if (activas === 2) return 'bifasico';
    if (activas === 1) return 'monofasico';
    return this.tensionesPorFase.some((v) => v !== null) ? 'sin_tension' : 'desconocido';
  }

  get tipoSuministroEtiqueta(): string {
    return ETIQUETA_SUMINISTRO[this.tipoSuministro];
  }

  /**
   * Tensión que corresponde mostrar según el tipo de suministro.
   *
   * En instalaciones de dos o tres fases se muestra la tensión entre fases,
   * que es la que ve la carga: en un sistema en estrella equivale a la tensión
   * de fase multiplicada por la raíz de tres. En monofásico se muestra la
   * única medida que hay.
   */
  get tensionMostrable(): number | null {
    const tensiones = this.tensionesPorFase;
    const activas = this.fasesActivas;
    if (activas.length === 0) return null;

    const media = activas.reduce((suma, i) => suma + (tensiones[i] ?? 0), 0) / activas.length;

    return activas.length === 1 ? media : media * RAIZ_3;
  }

  /** Corrientes de las fases alimentadas. No se combinan entre sí. */
  get corrientesActivas(): number[] {
    const corrientes = this.corrientesPorFase;
    return this.fasesActivas
      .map((i) => corrientes[i])
      .filter((a): a is number => a !== null && a !== undefined);
  }

  get potenciaTotal(): number | null {
    return this.valor('RMS_Watts_Tot');
  }

  get energiaTotal(): number | null {
    return this.valor('kWh_Tot');
  }

  get energiaInversa(): number | null {
    return this.valor('Rev_kWh_Tot');
  }

  get frecuencia(): number | null {
    return this.valor('Line_Freq');
  }

  /** Factor de potencia de cada fase. */
  get factorPotenciaPorFase(): (number | null)[] {
    return [1, 2, 3].map((i) => this.valor(`Cos_Theta_Ln_${i}`));
  }

  /** Estado de los relés del medidor, si los tiene. */
  estadoRele(numero: 1 | 2): boolean | null {
    const bruto = this.props.lecturas[`Relay_${numero}`];
    if (bruto === null || bruto === undefined || bruto === '') return null;

    const t = String(bruto).trim().toLowerCase();
    if (['1', 'on', 'true', 'closed', 'cerrado'].includes(t)) return true;
    if (['0', 'off', 'false', 'open', 'abierto'].includes(t)) return false;
    return null;
  }

  coincideCon(texto: string): boolean {
    const t = texto.trim().toLowerCase();
    if (!t) return true;
    return [
      this.props.nombre,
      this.props.serial,
      this.props.clienteNombre,
      this.props.gateway,
    ].some((campo) => campo?.toLowerCase().includes(t));
  }
}
